import styles from './styles.module.scss';

import { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Board } from '../../../../../../components/Board';
import { NavButton } from '../../../../../../components/NavButton';
import { ProfileBox } from '../../../../../../components/ProfileBox';
import { SelectionList } from '../../../../../../components/SelectionList';
import { SelectionItem } from '../../../../../../components/SelectionListItem';
import { verifyRoutePermissions } from '../../../../../../globalFunctions';
import { MessageContext } from '../../../../../../providers/MessageProvider';
import {
    User,
    LoadData,
    School,
    Error,
    SchoolClassOptions,
    SchoolClass,
    SchoolCourse
} from '../../../../../../types';
import { LogicManagerClassDetail } from '../LogicManagerClassDetail';
import { useOptions } from '../../../../../../hooks/useOptions';
import { LoadingContext } from '../../../../../../providers/LoadingProvider';
import { isEqual } from 'lodash';
import { AlertDialog, AlertDialogType } from '../../../../../../components/AlertDialog';
import { useSchoolClass } from '../../../../../../hooks/useSchoolClass';
import { SchoolClassDAO } from '../../../../../../classes/SchoolClassDAO';

export const LogicManagerClassMaintainRoute = () => {
    const [userAuth, setUserAuth] = useState<User>({} as User);
    const navigate = useNavigate();

    // Load data
    const options: LoadData = useOptions('class');
    const schoolClasses: LoadData = useSchoolClass();
    const [schoolClassOptions, setSchoolClassOptions] = useState<SchoolClassOptions>();
    const [schoolClassesList, setSchoolClassesList] = useState<SelectionItem[]>();
    const [loadedSchoolClasses, setLoadedSchoolClasses] = useState<SchoolClass[]>();
    const [selectedSchoolClass, setSelectedSchoolClass] = useState<SchoolClass>();

    // Handle changes
    const [openAlert, setOpenAlert] = useState<boolean>(false);
    const [alertDialogProps, setAlertDialogProps] = useState<AlertDialogType>();
    const [originalData, setOriginalData] = useState<SchoolClass>();
    const { setIsLoadingContent } = useContext(LoadingContext);
    const { setOpenMessage, setMessage } = useContext(MessageContext);

    const save = async () => {
        if (selectedSchoolClass) {
            try {
                setIsLoadingContent(true);

                const isNew = selectedSchoolClass.id === 0;
                const result = await SchoolClassDAO.save(
                    userAuth.token,
                    navigate,
                    selectedSchoolClass
                );

                if (
                    result &&
                    typeof result === 'object' &&
                    !Object.keys(result).includes('error')
                ) {
                    const currentSchoolClass = Object.assign({}, selectedSchoolClass);
                    currentSchoolClass.id = result.class.id;
                    currentSchoolClass.is_yours = result.class.is_yours;
                    currentSchoolClass.school = result.school;
                    currentSchoolClass.course = result.course;

                    if (result.isNewCourse) {
                        if (schoolClassOptions) {
                            schoolClassOptions.courses.push({
                                key: result.course.id,
                                value: result.course.id,
                                text: result.course.name,
                                description: result.course.slug
                            });

                            setSchoolClassOptions({ ...schoolClassOptions });
                        }
                    }

                    setSelectedSchoolClass({ ...currentSchoolClass });
                    setOriginalData({ ...currentSchoolClass });
                    updateSchoolClassesList({ ...currentSchoolClass }, false, isNew);
                    updateLoadedSchoolClasses({ ...currentSchoolClass }, false, isNew);

                    setMessage({
                        type: 'success',
                        content: isNew ? 'Turma criada com sucesso' : 'Turma alterada com sucesso'
                    });
                    setOpenMessage(true);
                } else {
                    throw result as Error;
                }
            } catch (error: any) {
                setMessage({
                    type: 'error',
                    content: error.message
                });
                setOpenMessage(true);
            } finally {
                setIsLoadingContent(false);
            }
        }
    };

    const reset = () => {
        if (originalData) {
            setSelectedSchoolClass({ ...originalData });
        }
    };

    const remove = async () => {
        if (selectedSchoolClass) {
            try {
                const response = await SchoolClassDAO.delete(
                    userAuth.token,
                    navigate,
                    selectedSchoolClass.id
                );

                if (response) {
                    setSelectedSchoolClass(undefined);
                    setOriginalData(undefined);
                    updateSchoolClassesList(selectedSchoolClass, true);
                    updateLoadedSchoolClasses(selectedSchoolClass, true);

                    setMessage({
                        type: 'success',
                        content: 'Turma excluída com sucesso'
                    });
                    setOpenMessage(true);
                } else {
                    throw response as any;
                }
            } catch (error: any) {
                setMessage({
                    type: 'error',
                    content: error.message
                });
                setOpenMessage(true);
            }
        }
    };

    const add = () => {
        const newSchoolClass: SchoolClass = {
            id: 0,
            name: '',
            level: {
                id: 0,
                name: ''
            },
            course: {
                id: 0,
                name: '',
                slug: ''
            } as SchoolCourse,
            year: '',
            students: [],
            teachers: [],
            school: {
                id: 0,
                slug: ''
            } as School,
            is_yours: false
        };

        setSelectedSchoolClass({ ...newSchoolClass });
        setOriginalData({ ...newSchoolClass });
    };

    const onAdd = () => {
        if (isEqual(selectedSchoolClass, originalData)) {
            add();
        } else {
            setAlertDialogProps({
                title: 'Confirmar alterações',
                message: 'Deseja salvar as alterações?',
                options: [
                    {
                        text: 'Desfazer',
                        action: reset,
                        type: 'error'
                    },
                    {
                        text: 'Salvar',
                        action: async () => await save(),
                        type: 'success'
                    }
                ]
            });
            setOpenAlert(true);
        }
    };

    const onSave = async () => {
        if (selectedSchoolClass) {
            try {
                setIsLoadingContent(true);

                if (validate(selectedSchoolClass)) {
                    if (!isEqual(selectedSchoolClass, originalData)) {
                        if (selectedSchoolClass.course.id === -1) {
                            setAlertDialogProps({
                                title: 'Confirmar novo curso',
                                message: (
                                    <>
                                        Você confirma a criação do curso&nbsp;
                                        <strong>
                                            {selectedSchoolClass.course.name} (
                                            {selectedSchoolClass.course.slug})
                                        </strong>
                                        &nbsp;e
                                        <br />
                                        certifica que ele ainda não está cadastrado na plataforma?
                                    </>
                                ),
                                options: [
                                    {
                                        text: 'Não tenho certeza',
                                        action: () => {},
                                        type: 'error'
                                    },
                                    {
                                        text: 'Confirmo',
                                        action: async () => await save(),
                                        type: 'success'
                                    }
                                ]
                            });
                            setOpenAlert(true);
                        } else {
                            await save();
                        }
                    } else {
                        setMessage({
                            type: 'info',
                            content: 'Sem alterações para salvar'
                        });
                        setOpenMessage(true);
                    }
                }
            } catch (error: any) {
                const e = error as Error;

                setMessage({
                    type: 'error',
                    content: e.message
                });
                setOpenMessage(true);
            } finally {
                setIsLoadingContent(false);
            }
        }
    };

    const onDelete = () => {
        setAlertDialogProps({
            title: 'Confirmar exclusão',
            message: 'Deseja realmente excluir esta turma?',
            options: [
                {
                    text: 'Excluir',
                    action: async () => await remove(),
                    type: 'error'
                }
            ]
        });
        setOpenAlert(true);
    };

    const validate = (schoolClass: SchoolClass) => {
        if (schoolClass.name.trim() === '') {
            setMessage({
                type: 'warning',
                content: 'Preencha o campo nome'
            });
            setOpenMessage(true);

            return false;
        }

        if (schoolClass.course.id === 0) {
            setMessage({
                type: 'warning',
                content: 'Selecione um curso para a turma'
            });
            setOpenMessage(true);

            return false;
        } else if (schoolClass.course.id === -1) {
            if (schoolClass.course.name.trim() === '') {
                setMessage({
                    type: 'warning',
                    content: 'Informe o nome do novo curso'
                });
                setOpenMessage(true);

                return false;
            }

            if (schoolClass.course.slug.trim() === '') {
                setMessage({
                    type: 'warning',
                    content: 'Informe a sigla do novo curso'
                });
                setOpenMessage(true);

                return false;
            }
        }

        if (schoolClass.year === '') {
            setMessage({
                type: 'warning',
                content: 'Selecione um ano letivo para a turma'
            });
            setOpenMessage(true);

            return false;
        }

        if (schoolClass.school.id === 0) {
            setMessage({
                type: 'warning',
                content: 'Selecione uma instituição para a turma'
            });
            setOpenMessage(true);

            return false;
        }

        return true;
    };

    const onSelectSchoolClass = (id: any) => {
        return new Promise<boolean>((resolve, reject) => {
            if (loadedSchoolClasses) {
                loadedSchoolClasses.forEach((schoolClass: SchoolClass) => {
                    if (schoolClass.id === id) {
                        if (
                            selectedSchoolClass &&
                            originalData &&
                            schoolClass.id !== originalData.id &&
                            !isEqual(selectedSchoolClass, originalData)
                        ) {
                            setAlertDialogProps({
                                title: 'Confirmar alterações',
                                message: 'Deseja salvar as alterações?',
                                options: [
                                    {
                                        text: 'Desfazer',
                                        action: reset,
                                        type: 'error'
                                    },
                                    {
                                        text: 'Salvar',
                                        action: async () => await save(),
                                        type: 'success'
                                    }
                                ]
                            });
                            setOpenAlert(true);

                            resolve(false);
                        } else {
                            const currentSelectedSchool = Object.assign({}, schoolClass);
                            setSelectedSchoolClass({ ...currentSelectedSchool });
                            setOriginalData({ ...currentSelectedSchool });

                            resolve(true);
                        }
                    }
                });
            }

            reject(false);
        });
    };

    const goBackwards = () => {
        if (isEqual(selectedSchoolClass, originalData)) {
            navigate(`/manager`);
        } else {
            setAlertDialogProps({
                title: 'Confirmar alterações',
                message: 'Deseja salvar as alterações?',
                options: [
                    {
                        text: 'Desfazer',
                        action: reset,
                        type: 'error'
                    },
                    {
                        text: 'Salvar',
                        action: async () => await save(),
                        type: 'success'
                    }
                ]
            });
            setOpenAlert(true);
        }
    };

    const updateSchoolClassesList = (
        currentSchoolClass: SchoolClass,
        wasRemoved: boolean = false,
        isNew: boolean = false
    ) => {
        if (schoolClassesList) {
            const schoolClassesData: SelectionItem[] = [];

            if (isNew) {
                schoolClassesData.push({
                    id: currentSchoolClass.id,
                    name: `${currentSchoolClass.year.toString()} - ${currentSchoolClass.name}`,
                    description: currentSchoolClass.is_yours
                        ? 'Você está vinculado'
                        : `${currentSchoolClass.school.slug}/${currentSchoolClass.course.slug}`
                });
            }

            schoolClassesList.forEach((schoolClass: SelectionItem) => {
                if (schoolClass.id === currentSchoolClass.id) {
                    if (!wasRemoved) {
                        schoolClassesData.push({
                            id: currentSchoolClass.id,
                            name: `${currentSchoolClass.year.toString()} - ${
                                currentSchoolClass.name
                            }`,
                            description: currentSchoolClass.is_yours
                                ? 'Você está vinculado'
                                : `${currentSchoolClass.school.slug}/${currentSchoolClass.course.slug}`
                        });
                    }
                } else {
                    schoolClassesData.push(schoolClass);
                }
            });

            setSchoolClassesList(schoolClassesData);
        }
    };

    const updateLoadedSchoolClasses = (
        currentSchoolClass: SchoolClass,
        wasRemoved: boolean = false,
        isNew: boolean = false
    ) => {
        if (loadedSchoolClasses) {
            const schoolClassesData: SchoolClass[] = [];

            if (isNew) {
                schoolClassesData.push(currentSchoolClass);
            }

            loadedSchoolClasses.forEach((schoolClass: SchoolClass) => {
                if (schoolClass.id === currentSchoolClass.id) {
                    if (!wasRemoved) {
                        schoolClassesData.push(currentSchoolClass);
                    }
                } else {
                    schoolClassesData.push(schoolClass);
                }
            });

            setLoadedSchoolClasses(schoolClassesData);
        }
    };

    useEffect(() => {
        if (!schoolClasses.isLoading) {
            const data = schoolClasses.data;

            if (data && typeof data === 'object' && !Object.keys(data).includes('error')) {
                const schoolsData: SelectionItem[] = [];

                schoolClasses.data.forEach((schoolClass: SchoolClass) => {
                    schoolsData.push({
                        id: schoolClass.id,
                        name: `${schoolClass.year.toString()} - ${schoolClass.name}`,
                        description: schoolClass.is_yours
                            ? 'Você está vinculado'
                            : `${schoolClass.school.slug}/${schoolClass.course.slug}`
                    });
                });

                setSchoolClassesList(schoolsData);
                setLoadedSchoolClasses(data);
            } else {
                const error = data as Error;

                setMessage({
                    type: 'error',
                    content: error.message
                });
                setOpenMessage(true);
            }
        }
    }, [schoolClasses.isLoading]);

    useEffect(() => {
        if (!options.isLoading) {
            const data = options.data;

            if (data && typeof data === 'object' && !Object.keys(data).includes('error')) {
                setSchoolClassOptions(data);
            } else {
                const error = data as Error;

                setMessage({
                    type: 'error',
                    content: error.message
                });
                setOpenMessage(true);
            }
        }
    }, [options.isLoading]);

    useEffect(() => {
        const user = verifyRoutePermissions(
            ['auth', 'email.verified', 'not.pending.update', 'logicmanager.classes'],
            navigate
        );

        if (typeof user === 'object') {
            setUserAuth(user);
        }
    }, []);

    return (
        <div className={styles.wrapper}>
            <NavButton
                direction='left'
                style={{ left: '1vw', top: '2vw', zIndex: '20' }}
                onClick={goBackwards}
            />
            {userAuth.token && schoolClassesList && schoolClassOptions && (
                <>
                    <ProfileBox user={userAuth} />
                    <Board wrapperClass={styles.board}>
                        <div className={styles.boardWrapper}>
                            <h1 className={styles.title}>Turmas</h1>

                            <div className={styles.editContainer}>
                                <SelectionList
                                    items={schoolClassesList}
                                    defaultSelectedItem={
                                        selectedSchoolClass &&
                                        ({
                                            id: selectedSchoolClass.id,
                                            name: `${selectedSchoolClass.year.toString()} - ${
                                                selectedSchoolClass.name
                                            }`,
                                            description: selectedSchoolClass.is_yours
                                                ? 'Você está vinculado'
                                                : `${selectedSchoolClass.school.slug}/${selectedSchoolClass.course.slug}`
                                        } as SelectionItem)
                                    }
                                    onSelectItem={onSelectSchoolClass}
                                    searchPlaceholder='Pesquisar'
                                    wrapperClass={styles.list}
                                />
                                <LogicManagerClassDetail
                                    schoolClass={selectedSchoolClass}
                                    setSchoolClass={setSelectedSchoolClass}
                                    options={schoolClassOptions}
                                    onSave={onSave}
                                    onAdd={onAdd}
                                    onDelete={onDelete}
                                    userAuth={userAuth}
                                />
                            </div>
                        </div>
                    </Board>
                </>
            )}
            {alertDialogProps && (
                <AlertDialog
                    setOpen={setOpenAlert}
                    open={openAlert}
                    title={alertDialogProps.title}
                    message={alertDialogProps.message}
                    options={alertDialogProps.options}
                />
            )}
        </div>
    );
};
